import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  WorkOrder, 
  WorkOrderSerial, 
  WorkOrderOperationLog,
  WorkOrderStatus,
  RouteCard,
  SearchWorkOrdersRequest,
  SearchWorkOrdersResponse,
  CreateWorkOrderRequest,
  OperationLogRequest
} from '../../services/workOrderApi';

interface WorkOrderState {
  // Work Orders
  workOrders: WorkOrder[];
  selectedWorkOrder: WorkOrder | null;
  loading: boolean;
  error: string | null;
  
  // Pagination
  currentPage: number;
  pageSize: number;
  totalCount: number;
  
  // Filters
  searchTerm: string;
  statusFilter: number | null;
  isReworkFilter: boolean | null;
  
  // Dropdown data
  workOrderStatuses: WorkOrderStatus[];
  routeCards: RouteCard[];
  loadingDropdowns: boolean;
  
  // Operation tracking
  activeOperationLogs: WorkOrderOperationLog[];
  operationLogsLoading: boolean;
  
  // Serial management
  selectedSerials: WorkOrderSerial[];
  serialsLoading: boolean;
  
  // Real-time updates
  liveUpdates: boolean;
  lastUpdateTimestamp: string | null;
}

const initialState: WorkOrderState = {
  workOrders: [],
  selectedWorkOrder: null,
  loading: false,
  error: null,
  
  currentPage: 1,
  pageSize: 25,
  totalCount: 0,
  
  searchTerm: '',
  statusFilter: null,
  isReworkFilter: false,
  
  workOrderStatuses: [],
  routeCards: [],
  loadingDropdowns: false,
  
  activeOperationLogs: [],
  operationLogsLoading: false,
  
  selectedSerials: [],
  serialsLoading: false,
  
  liveUpdates: true,
  lastUpdateTimestamp: null,
};

const workOrderSlice = createSlice({
  name: 'workOrder',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    setDropdownsLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingDropdowns = action.payload;
    },
    
    setOperationLogsLoading: (state, action: PayloadAction<boolean>) => {
      state.operationLogsLoading = action.payload;
    },
    
    setSerialsLoading: (state, action: PayloadAction<boolean>) => {
      state.serialsLoading = action.payload;
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // Work Orders CRUD
    fetchWorkOrdersStart: (state, action: PayloadAction<SearchWorkOrdersRequest | undefined>) => {
      state.loading = true;
      state.error = null;
    },
    
    fetchWorkOrdersSuccess: (state, action: PayloadAction<SearchWorkOrdersResponse>) => {
      console.log('WorkOrder Reducer: fetchWorkOrdersSuccess called with payload:', action.payload);
      console.log('WorkOrder Reducer: action.payload.data:', action.payload.data);
      console.log('WorkOrder Reducer: action.payload.data length:', action.payload.data?.length);
      console.log('WorkOrder Reducer: action.payload.data is array:', Array.isArray(action.payload.data));
      console.log('WorkOrder Reducer: action.payload keys:', Object.keys(action.payload));
      
      state.loading = false;
      
      // Safely handle the data
      if (action.payload && action.payload.data && Array.isArray(action.payload.data)) {
        state.workOrders = action.payload.data;
        state.totalCount = action.payload.totalCount || 0;
        state.currentPage = action.payload.pageNum || 1;
        state.pageSize = action.payload.pageSize || 25;
      } else {
        console.warn('WorkOrder Reducer: Invalid payload structure, setting empty state');
        state.workOrders = [];
        state.totalCount = 0;
        state.currentPage = 1;
        state.pageSize = 25;
      }
      
      state.lastUpdateTimestamp = new Date().toISOString();
      
      console.log('WorkOrder Reducer: State updated - workOrders length:', state.workOrders.length);
      console.log('WorkOrder Reducer: State updated - totalCount:', state.totalCount);
      console.log('WorkOrder Reducer: State updated - currentPage:', state.currentPage);
      console.log('WorkOrder Reducer: State updated - pageSize:', state.pageSize);
    },
    
    fetchWorkOrdersFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    
    // Single Work Order
    fetchWorkOrderByIdStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    
    fetchWorkOrderByIdSuccess: (state, action: PayloadAction<WorkOrder>) => {
      state.loading = false;
      state.selectedWorkOrder = action.payload;
    },
    
    fetchWorkOrderByIdFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    
    // Create Work Order
    createWorkOrderStart: (state, action: PayloadAction<CreateWorkOrderRequest>) => {
      state.loading = true;
      state.error = null;
    },
    
    createWorkOrderSuccess: (state, action: PayloadAction<WorkOrder>) => {
      state.loading = false;
      state.workOrders.unshift(action.payload);
      state.totalCount += 1;
    },
    
    createWorkOrderFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    
    // Update Work Order
    updateWorkOrderStart: (state, action: PayloadAction<Partial<WorkOrder>>) => {
      state.loading = true;
      state.error = null;
    },
    
    updateWorkOrderSuccess: (state, action: PayloadAction<WorkOrder>) => {
      state.loading = false;
      const index = state.workOrders.findIndex(wo => wo.id === action.payload.id);
      if (index !== -1) {
        state.workOrders[index] = action.payload;
      }
      if (state.selectedWorkOrder?.id === action.payload.id) {
        state.selectedWorkOrder = action.payload;
      }
    },
    
    updateWorkOrderFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    
    // Delete Work Order
    deleteWorkOrderStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    
    deleteWorkOrderSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.workOrders = state.workOrders.filter(wo => wo.id !== action.payload);
      state.totalCount -= 1;
      if (state.selectedWorkOrder?.id === action.payload) {
        state.selectedWorkOrder = null;
      }
    },
    
    deleteWorkOrderFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    
    // Dropdown data
    fetchDropdownsStart: (state) => {
      state.loadingDropdowns = true;
    },
    
    fetchDropdownsSuccess: (state, action: PayloadAction<{
      workOrderStatuses: WorkOrderStatus[];
      routeCards: RouteCard[];
    }>) => {
      state.loadingDropdowns = false;
      state.workOrderStatuses = action.payload.workOrderStatuses;
      state.routeCards = action.payload.routeCards;
    },
    
    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.loadingDropdowns = false;
      state.error = action.payload;
    },
    
    // Operation Logs
    fetchOperationLogsStart: (state, action: PayloadAction<string>) => {
      state.operationLogsLoading = true;
    },
    
    fetchOperationLogsSuccess: (state, action: PayloadAction<WorkOrderOperationLog[]>) => {
      state.operationLogsLoading = false;
      state.activeOperationLogs = action.payload;
    },
    
    fetchOperationLogsFailure: (state, action: PayloadAction<string>) => {
      state.operationLogsLoading = false;
      state.error = action.payload;
    },
    
    // Create Operation Log
    createOperationLogStart: (state, action: PayloadAction<OperationLogRequest>) => {
      // No loading state for individual operation logs to avoid UI blocking
    },
    
    createOperationLogSuccess: (state, action: PayloadAction<WorkOrderOperationLog>) => {
      state.activeOperationLogs.unshift(action.payload);
    },
    
    createOperationLogFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    
    // Update Operation Log
    updateOperationLogStart: (state, action: PayloadAction<Partial<WorkOrderOperationLog>>) => {
      // No loading state for individual operation logs
    },
    
    updateOperationLogSuccess: (state, action: PayloadAction<WorkOrderOperationLog>) => {
      const index = state.activeOperationLogs.findIndex(log => log.id === action.payload.id);
      if (index !== -1) {
        state.activeOperationLogs[index] = action.payload;
      }
    },
    
    updateOperationLogFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    
    // Serial management
    fetchSerialsStart: (state, action: PayloadAction<string>) => {
      state.serialsLoading = true;
    },
    
    fetchSerialsSuccess: (state, action: PayloadAction<WorkOrderSerial[]>) => {
      state.serialsLoading = false;
      state.selectedSerials = action.payload;
    },
    
    fetchSerialsFailure: (state, action: PayloadAction<string>) => {
      state.serialsLoading = false;
      state.error = action.payload;
    },
    
    // Serial actions
    approveSerialStart: (state, action: PayloadAction<{ workOrderId: string; serialNum: string }>) => {
      // Find and update serial status optimistically
      const serial = state.selectedSerials.find(s => s.SerialNum === action.payload.serialNum);
      if (serial) {
        // Could add a status field to track pending approval
      }
    },
    
    approveSerialSuccess: (state, action: PayloadAction<string>) => {
      // Remove approved serial from selected serials
      state.selectedSerials = state.selectedSerials.filter(s => s.SerialNum !== action.payload);
    },
    
    rejectSerialStart: (state, action: PayloadAction<{ workOrderId: string; serialNum: string; reason?: string }>) => {
      // Optimistic update
    },
    
    rejectSerialSuccess: (state, action: PayloadAction<string>) => {
      // Remove rejected serial from selected serials
      state.selectedSerials = state.selectedSerials.filter(s => s.SerialNum !== action.payload);
    },
    
    // Filters and search
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1; // Reset to first page when searching
    },
    
    setStatusFilter: (state, action: PayloadAction<number | null>) => {
      state.statusFilter = action.payload;
      state.currentPage = 1;
    },
    
    setIsReworkFilter: (state, action: PayloadAction<boolean | null>) => {
      state.isReworkFilter = action.payload;
      state.currentPage = 1;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    
    // Real-time updates
    toggleLiveUpdates: (state) => {
      state.liveUpdates = !state.liveUpdates;
    },
    
    setLiveUpdates: (state, action: PayloadAction<boolean>) => {
      state.liveUpdates = action.payload;
    },
    
    // Real-time data updates from WebSocket
    updateWorkOrderRealtime: (state, action: PayloadAction<WorkOrder>) => {
      const index = state.workOrders.findIndex(wo => wo.id === action.payload.id);
      if (index !== -1) {
        state.workOrders[index] = action.payload;
      }
      if (state.selectedWorkOrder?.id === action.payload.id) {
        state.selectedWorkOrder = action.payload;
      }
      state.lastUpdateTimestamp = new Date().toISOString();
    },
    
    addOperationLogRealtime: (state, action: PayloadAction<WorkOrderOperationLog>) => {
      // Add to beginning of array (most recent first)
      state.activeOperationLogs.unshift(action.payload);
      // Keep only the last 20 logs to prevent memory issues
      if (state.activeOperationLogs.length > 20) {
        state.activeOperationLogs = state.activeOperationLogs.slice(0, 20);
      }
      state.lastUpdateTimestamp = new Date().toISOString();
    },
    
    // Clear selected data
    clearSelectedWorkOrder: (state) => {
      state.selectedWorkOrder = null;
    },
    
    clearSelectedSerials: (state) => {
      state.selectedSerials = [];
    },
    
    clearOperationLogs: (state) => {
      state.activeOperationLogs = [];
    },
    
    // Reset state
    resetWorkOrderState: (state) => {
      return initialState;
    },
  },
});

export const {
  setLoading,
  setDropdownsLoading,
  setOperationLogsLoading,
  setSerialsLoading,
  setError,
  clearError,
  
  fetchWorkOrdersStart,
  fetchWorkOrdersSuccess,
  fetchWorkOrdersFailure,
  
  fetchWorkOrderByIdStart,
  fetchWorkOrderByIdSuccess,
  fetchWorkOrderByIdFailure,
  
  createWorkOrderStart,
  createWorkOrderSuccess,
  createWorkOrderFailure,
  
  updateWorkOrderStart,
  updateWorkOrderSuccess,
  updateWorkOrderFailure,
  
  deleteWorkOrderStart,
  deleteWorkOrderSuccess,
  deleteWorkOrderFailure,
  
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  
  fetchOperationLogsStart,
  fetchOperationLogsSuccess,
  fetchOperationLogsFailure,
  
  createOperationLogStart,
  createOperationLogSuccess,
  createOperationLogFailure,
  
  updateOperationLogStart,
  updateOperationLogSuccess,
  updateOperationLogFailure,
  
  fetchSerialsStart,
  fetchSerialsSuccess,
  fetchSerialsFailure,
  
  approveSerialStart,
  approveSerialSuccess,
  rejectSerialStart,
  rejectSerialSuccess,
  
  setSearchTerm,
  setStatusFilter,
  setIsReworkFilter,
  setPage,
  setPageSize,
  
  toggleLiveUpdates,
  setLiveUpdates,
  
  updateWorkOrderRealtime,
  addOperationLogRealtime,
  
  clearSelectedWorkOrder,
  clearSelectedSerials,
  clearOperationLogs,
  resetWorkOrderState,
} = workOrderSlice.actions;

export default workOrderSlice.reducer; 